import { CdkMenu, CdkMenuItem } from '@angular/cdk/menu';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { AuthService, ImpersonationService } from '../../domains/auth';
import { ImpersonationDialogComponent } from '../../domains/auth/impersonation-dialog/impersonation-dialog.component';
import { LayoutService } from '../layout.service';
import { TopBarButtonComponent } from './top-bar-button/top-bar-button.component';
import { SidebarService } from '../sidebar/sidebar.service';
import { PermissionService } from '../../core/security';
import { SubscriberChipSelectComponent, SubscriberChipSize } from './subscriber-chip-select/subscriber-chip-select.component';
import { UserService } from '../../domains/user';

// Single responsive boundary (DESIGN-SYSTEM.md §12.1), measured on the bar's own rendered
// width (not the viewport) so the bar also reacts inside presentation/embedded shells.
// At or above it the bar is FULL; below it (NARROW) the brand hides, the chip goes icon-only,
// and the personal controls fold into the overflow ⋮ menu.
const FULL_MIN_WIDTH_PX = 600;

@Component({
    selector: 'app-top-bar',
    imports: [CdkMenu, CdkMenuItem, ImpersonationDialogComponent, TopBarButtonComponent, SubscriberChipSelectComponent],
    templateUrl: './top-bar.component.html',
    styleUrl: './top-bar.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopBarComponent implements AfterViewInit, OnDestroy {
    private layoutService = inject(LayoutService);
    private sidebarService = inject(SidebarService);
    private authService = inject(AuthService);
    private impersonationService = inject(ImpersonationService);
    private permissionService = inject(PermissionService);
    private userService = inject(UserService);

    private readonly barElement = viewChild.required<ElementRef<HTMLElement>>('topBar');
    private readonly observedBarWidth = signal(0);
    private resizeObserver: ResizeObserver | null = null;

    isDarkMode = this.layoutService.isDarkMode;
    isPresentationMode = this.layoutService.isPresentationMode;
    activeSubscriberKey = this.layoutService.activeSubscriberKey;

    // Width 0 = not yet measured; treat as FULL so first paint doesn't flash the collapsed layout.
    isFull = computed(() => {
        const width = this.observedBarWidth();

        return width === 0 || width >= FULL_MIN_WIDTH_PX;
    });

    chipSize = computed<SubscriberChipSize>(() => (this.isFull() ? 'full' : 'icon'));

    presentationModeAvailable = computed(() => this.permissionService.hasPermission('sysAdmin'));
    impersonationAvailable = computed(() => this.permissionService.hasPermission('sysAdmin'));
    impersonationMessage = computed(() => {
        if (!this.impersonationService.isImpersonating()) {
            return null;
        }

        const user = this.userService.userContext();
        const userName = user?.name || user?.userName || 'Unknown User';
        const subscriberName = user?.subscriber?.subscriberName || 'Unknown Subscriber';

        return `Impersonating ${userName} at ${subscriberName}`;
    });
    isImpersonationDialogVisible = signal<boolean>(false);

    ngAfterViewInit() {
        const bar = this.barElement().nativeElement;

        // Seed synchronously — ResizeObserver's first callback lands after first paint.
        this.updateObservedBarWidth(bar.getBoundingClientRect().width);

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
            const entry = entries.find(({ target }) => target === bar);
            const width = entry?.borderBoxSize?.[0]?.inlineSize ?? entry?.contentRect.width ?? 0;

            this.updateObservedBarWidth(width);
        });
        this.resizeObserver.observe(bar, { box: 'border-box' });
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }

    onMenuToggleClicked(event: Event) {
        // Prevent the opening click from immediately triggering the sidebar's outside-click handler.
        event.stopPropagation();
        this.sidebarService.toggleSidebar();
    }

    onSignOutClicked() {
        this.authService.signOut().subscribe({error: err => console.error(err)});
    }

    onImpersonateUserClicked() {
        this.isImpersonationDialogVisible.set(true);
    }

    onDarkModeToggleClicked() {
        this.layoutService.toggleDarkMode();
    }

    onPresentationModeToggleClicked() {
        this.layoutService.togglePresentationMode();
    }

    onSubscriberKeyChanged(subscriberKey: number | null) {
        this.layoutService.setActiveSubscriberKey(subscriberKey);
    }

    private updateObservedBarWidth(width: number) {
        if (width <= 0) {
            return;
        }

        this.observedBarWidth.set(width);
    }
}
